/* eslint-disable */
export type UtmParams = {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
    id?: string;
};

export type ClickIds = {
    gclid?: string;
    fbclid?: string;
    msclkid?: string;
    ttclid?: string;
};

export type DeviceInfo = {
    device: 'mobile' | 'tablet' | 'desktop' | 'unknown';
    os: string;
    browser: string;
};

export type GeoInfo = {
    country?: string; // ISO region if available (e.g., US)
    language?: string; // e.g., en-US
    timezone?: string; // e.g., America/New_York
};

export type AttributionPayload = {
    user_id: string;
    captured_at: string; // ISO timestamp
    landing_page: string;
    referrer: string;
    utm: UtmParams;
    click_ids: ClickIds;
    device_info: DeviceInfo;
    geo: GeoInfo;
};

const LOWERCASE_KEYS: Array<keyof UtmParams> = ['source', 'medium', 'campaign', 'term', 'content', 'id'];

export const getOrCreateAttributionUserId = (): string => {
    const STORAGE_KEY = 'attribution_user_id';
    let existing = localStorage.getItem(STORAGE_KEY);
    if (existing && existing.trim().length > 0) return existing;
    // Simple UUID v4 generator
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    localStorage.setItem(STORAGE_KEY, uuid);
    return uuid;
};

const normalize = (value?: string | null): string | undefined => {
    if (value == null) return undefined;
    const trimmed = String(value).trim();
    if (!trimmed) return undefined;
    return trimmed.toLowerCase();
};

export const parseUtmAndClickIds = (searchParams: URLSearchParams): { utm: UtmParams; click_ids: ClickIds } => {
    const utm: UtmParams = {};
    const click_ids: ClickIds = {};

    const utmMap: Record<string, keyof UtmParams> = {
        utm_source: 'source',
        utm_medium: 'medium',
        utm_campaign: 'campaign',
        utm_term: 'term',
        utm_content: 'content',
        utm_id: 'id'
    };

    Object.entries(utmMap).forEach(([param, key]) => {
        const value = normalize(searchParams.get(param));
        if (value) utm[key] = value;
    });

    const gclid = normalize(searchParams.get('gclid'));
    const fbclid = normalize(searchParams.get('fbclid'));
    const msclkid = normalize(searchParams.get('msclkid'));
    const ttclid = normalize(searchParams.get('ttclid'));
    if (gclid) click_ids.gclid = gclid;
    if (fbclid) click_ids.fbclid = fbclid;
    if (msclkid) click_ids.msclkid = msclkid;
    if (ttclid) click_ids.ttclid = ttclid;

    return { utm, click_ids };
};

export const getDeviceInfo = (userAgentString?: string): DeviceInfo => {
    const ua = (userAgentString || navigator.userAgent || '').toLowerCase();
    let device: DeviceInfo['device'] = 'unknown';
    if (/mobi|iphone|ipod|android/.test(ua)) device = 'mobile';
    if (/ipad|tablet/.test(ua)) device = 'tablet';
    if (device === 'unknown') device = 'desktop';

    let os = 'unknown';
    if (/windows nt/.test(ua)) os = 'windows';
    else if (/mac os x/.test(ua)) os = 'macos';
    else if (/android/.test(ua)) os = 'android';
    else if (/iphone|ipad|ipod/.test(ua)) os = 'ios';
    else if (/linux/.test(ua)) os = 'linux';

    let browser = 'unknown';
    if (/edg\//.test(ua)) browser = 'edge';
    else if (/chrome\//.test(ua) && !/edg\//.test(ua)) browser = 'chrome';
    else if (/safari\//.test(ua) && !/chrome\//.test(ua)) browser = 'safari';
    else if (/firefox\//.test(ua)) browser = 'firefox';

    return { device, os, browser };
};

export const getGeoInfo = (): GeoInfo => {
    const language = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let country: string | undefined = undefined;
    try {
        // Try to extract region from language like en-US
        const parts = language.split('-');
        if (parts.length >= 2 && parts[1]) country = parts[1].toUpperCase();
    } catch (_) {
        // ignore
    }
    return { country, language, timezone };
};

export const buildAttributionPayload = (): AttributionPayload => {
    const user_id = getOrCreateAttributionUserId();
    const url = new URL(window.location.href);
    const { utm, click_ids,  } = parseUtmAndClickIds(url.searchParams);
    const landing_page = window.location.href;
    const referrer = document.referrer || '';
    const captured_at = new Date().toISOString();
    const device_info = getDeviceInfo();
    const geo = getGeoInfo();

    // Ensure utm keys are normalized
    const normalizedUtm: UtmParams = {};
    LOWERCASE_KEYS.forEach((k) => {
        if (utm[k]) normalizedUtm[k] = utm[k];
    });

    return {
        user_id,
        captured_at,
        landing_page,
        referrer,
        utm: normalizedUtm,
        click_ids,
        device_info,
        geo,
    };
};


